import { Button, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import type {
  ActiveKeyspacePointer,
  KeyspaceDirectoryEntry,
} from "@legal-500/redis-db-manager-types";
import {
  dbManagerService,
  SEARCH_INDEX_NOT_READY,
  type SearchIndexNotReadyDetails,
} from "../../../api/dbManager.service.js";
import type { ApiError } from "../../../api/client.js";
import { formatFailures, formatIndexedPercent } from "./searchIndexStatus.js";
import styles from "./DbManager.module.css";

type SearchIndexConflict = {
  details: SearchIndexNotReadyDetails | null;
  message: string;
};

type MakeActiveModalProps = {
  isOpen: boolean;
  targetEntry: KeyspaceDirectoryEntry;
  currentActive: ActiveKeyspacePointer | null;
  missingPageTypes: string[];
  onClose: () => void;
  onActivated: (newActive: ActiveKeyspacePointer, warning?: string) => void;
};

export function MakeActiveModal({
  isOpen,
  targetEntry,
  currentActive,
  missingPageTypes,
  onClose,
  onActivated,
}: MakeActiveModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchIndexConflict, setSearchIndexConflict] = useState<SearchIndexConflict | null>(null);

  useEffect(() => {
    if (isOpen) return;
    setSearchIndexConflict(null);
    setError(null);
  }, [isOpen]);

  const isPartial = missingPageTypes.length > 0;

  const activate = async (force: boolean) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await dbManagerService.setActiveKeyspace(
        targetEntry.keyspace,
        targetEntry.family,
        { force },
      );
      setSearchIndexConflict(null);
      onActivated(response.activeKeyspace, response.warning);
    } catch (err) {
      const conflict = force ? null : toSearchIndexConflict(err);
      setSearchIndexConflict(conflict);
      if (!conflict) setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (searchIndexConflict) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Search index isn't ready"
        size="small"
        blurBackground
      >
        <SearchIndexConflictConfirm
          keyspace={targetEntry.keyspace}
          conflict={searchIndexConflict}
          submitting={submitting}
          onCancel={() => setSearchIndexConflict(null)}
          onConfirm={() => activate(true)}
        />
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Make ${targetEntry.keyspace} active`}
      size="medium"
      blurBackground
    >
      <div className={styles.modalBody}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        {isPartial && (
          <div className={styles.warningBanner} data-testid="make-active-partial-warning">
            <strong>This slot is partial.</strong> The following page types are missing — production
            may have gaps if you continue.
            <div className={styles.missingPageList}>
              {missingPageTypes.map(name => (
                <span key={name} className={styles.missingPageChip}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className={styles.confirmationLine}>
          <span className={styles.confirmationKey}>From</span>
          <span className={styles.confirmationValue}>
            {currentActive?.mode === "slot" ? currentActive.slot : "legacy"}
          </span>
        </div>

        <div className={styles.confirmationLine}>
          <span className={styles.confirmationKey}>To</span>
          <span className={styles.confirmationValue}>
            <code>{targetEntry.keyspace}</code>
          </span>
        </div>

        <p>
          Production traffic will start serving from slot <code>{targetEntry.keyspace}</code>{" "}
          immediately. The previous active slot will remain available for rollback.
        </p>

        <div className={styles.actions}>
          <Button variant="text" color="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => activate(false)}
            disabled={submitting}
          >
            {submitting ? "Activating..." : "Make active"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type SearchIndexConflictConfirmProps = {
  keyspace: string;
  conflict: SearchIndexConflict;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

function SearchIndexConflictConfirm({
  keyspace,
  conflict,
  submitting,
  onCancel,
  onConfirm,
}: SearchIndexConflictConfirmProps) {
  const { details } = conflict;
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <div className={styles.modalBody}>
      {details ? (
        <>
          <div className={styles.confirmationLine}>
            <span className={styles.confirmationKey}>Documents</span>
            <span className={styles.confirmationValue}>
              {details.numDocs === null ? "unknown" : details.numDocs.toLocaleString()}
            </span>
          </div>
          <div className={styles.confirmationLine}>
            <span className={styles.confirmationKey}>Indexed</span>
            <span className={styles.confirmationValue}>
              {details.percentIndexed === null
                ? "unknown"
                : formatIndexedPercent(details.percentIndexed)}
            </span>
          </div>
          <div className={styles.confirmationLine}>
            <span className={styles.confirmationKey}>Failures</span>
            <span className={styles.confirmationValue}>
              {details.hashIndexingFailures === null
                ? "unknown"
                : formatFailures(details.hashIndexingFailures)}
            </span>
          </div>
          <div className={styles.warningBanner}>
            <ul className={styles.searchIndexReasonList}>
              {details.reasons.map(reason => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>
        </>
      ) : (
        <div className={styles.warningBanner}>{conflict.message}</div>
      )}

      <p>
        If you make <code>{keyspace}</code> active now, search on the live site would return partial
        results.
      </p>

      <div className={styles.actions}>
        <Button
          ref={cancelRef}
          variant="text"
          color="default"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={submitting}>
          {submitting ? "Activating..." : "Activate anyway"}
        </Button>
      </div>
    </div>
  );
}

function toSearchIndexConflict(err: unknown): SearchIndexConflict | null {
  if (!(err instanceof Error)) return null;
  const apiError: ApiError = err;
  if (apiError.status !== 409 || apiError.code !== SEARCH_INDEX_NOT_READY) return null;
  return {
    details: isSearchIndexNotReadyDetails(apiError.detailsRaw) ? apiError.detailsRaw : null,
    message: apiError.message,
  };
}

function isNullableFiniteNumber(value: unknown): boolean {
  return value === null || (typeof value === "number" && Number.isFinite(value));
}

function isSearchIndexNotReadyDetails(value: unknown): value is SearchIndexNotReadyDetails {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    isNullableFiniteNumber(candidate.numDocs) &&
    isNullableFiniteNumber(candidate.percentIndexed) &&
    isNullableFiniteNumber(candidate.hashIndexingFailures) &&
    Array.isArray(candidate.reasons) &&
    candidate.reasons.length > 0 &&
    candidate.reasons.every(reason => typeof reason === "string")
  );
}
